/**
 * Phase 2/3 shared component — record an externally-signed instrument.
 *
 * Phase A multi-file (2026-11): accepts one or more PDFs per call.
 * The server allows up to 25 MiB per file; the modal enforces a soft
 * cap of 10 files per bundle.
 *
 * UI redesign (2026-11, in-flight dispatch): header/body/footer
 * three-pane scrollable layout, rounded-xl card chrome, grey-wrapped
 * summary section, terracotta accent (#A8543F) for interactive bits.
 *
 * Used by:
 *   • WS3 FDA upload (B6Pages)
 *   • WS5 paid-services execution (B8Pages)
 *   • WS6 contract execution (B9B10Pages)
 *
 * Props:
 *   open             boolean      — modal visibility
 *   onClose          () => void
 *   endpointUrl      string       — the admin POST endpoint
 *   onSuccessData    (data) => void — receives the server JSON
 *   submitLabel      string       — e.g. "Record Execution" | "Upload Signed Agreement"
 *   helpText         string       — hint shown under the title
 *   testIdPrefix     string       — e.g. "ws6-rec-ext-exec" | "ws3-rec-ext-exec"
 *   projectId        string?      — enables the "Populate from Project" button
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Upload, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

// Shared accent constant — matches the admin surface's terracotta.
const ACCENT = "#A8543F";
const ACCENT_DARK = "#8B4535";

export const EXT_METHOD_LABELS = {
    zoho_sign: "Zoho Sign",
    docusign: "DocuSign",
    hand_signed: "Hand signed",
    other_e_sign: "Other e-sign platform",
    other: "Other",
};

export const MAX_FILE_BYTES = 25 * 1024 * 1024;         // 25 MiB per file
export const MAX_FILES_SOFT_CAP = 10;                   // soft cap per bundle

export function uuid4() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "op-" + Date.now().toString(36) + "-" +
           Math.random().toString(36).slice(2, 10) + "-" +
           Math.random().toString(36).slice(2, 10);
}

function _emptySigner() {
    return {
        signer_name: "",
        signer_email: "",
        signer_role: "",
        signed_on: new Date().toISOString().slice(0, 10),
        signature_method: "zoho_sign",
        notes: "",
    };
}

function _signerHasData(s) {
    return Boolean(
        (s.signer_name || "").trim() ||
        (s.signer_email || "").trim() ||
        (s.signer_role || "").trim() ||
        (s.notes || "").trim());
}

function _fmtSize(bytes) {
    if (bytes == null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function _validateFile(f) {
    const nameLower = (f.name || "").toLowerCase();
    const typeOk = f.type === "application/pdf" || nameLower.endsWith(".pdf");
    if (!typeOk) return "Must be a PDF.";
    if (f.size > MAX_FILE_BYTES) {
        return `Exceeds 25 MiB (file is ${_fmtSize(f.size)}).`;
    }
    return "";
}

// Shared input chrome used by every form control inside the signer
// cards and the Summary section.  Terracotta focus ring + border.
const INPUT_CLS =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 " +
    "placeholder-gray-400 transition-shadow focus:outline-none " +
    "focus:border-[#A8543F] focus:ring-1 focus:ring-[#A8543F]";

const LABEL_CLS = "block text-xs font-medium text-gray-700 mb-1.5";

export default function RecordExternalExecutionModal({
    open, onClose, endpointUrl, onSuccessData,
    submitLabel = "Record Execution",
    helpText = ("Attach one or more externally-signed PDFs and capture "
                + "structured signer evidence."),
    testIdPrefix = "rec-ext-exec",
    projectId = null,
}) {
    const [opId] = useState(() => uuid4());
    const [pdfFiles, setPdfFiles] = useState([]);
    const [signers, setSigners] = useState([_emptySigner()]);
    const [methodSummary, setMethodSummary] = useState("");
    const [externalRef, setExternalRef] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [isDragOver, setDragOver] = useState(false);
    const [signerDefaults, setSignerDefaults] = useState(null);
    const [populating, setPopulating] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open || !projectId || signerDefaults !== null) return;
        api.get("/admin/config/signer-defaults")
            .then(r => setSignerDefaults(r.data))
            .catch(() => setSignerDefaults({}));
    }, [open, projectId, signerDefaults]);

    if (!open) return null;

    const updateSigner = (idx, patch) =>
        setSigners(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
    const addSigner = () => setSigners(prev => [...prev, _emptySigner()]);
    const removeSigner = (idx) =>
        setSigners(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

    const addFiles = (incoming) => {
        if (!incoming || incoming.length === 0) return;
        setPdfFiles(prev => [...prev, ...Array.from(incoming)]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };
    const removeFile = (idx) => {
        setPdfFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const onDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragOver(false);
        if (busy) return;
        addFiles(e.dataTransfer?.files);
    };
    const onDragOver = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (!busy) setDragOver(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragOver(false);
    };

    const populateFromProject = async () => {
        if (!projectId || populating) return;
        const hasDirty = signers.some(_signerHasData);
        if (hasDirty) {
            const ok = window.confirm(
                "Replace current signers with project defaults?");
            if (!ok) return;
        }
        setPopulating(true);
        setErr("");
        try {
            const [wsResp, defaultsResp] = await Promise.all([
                api.get(`/admin/projects/${projectId}/workspace`),
                signerDefaults
                    ? Promise.resolve({ data: signerDefaults })
                    : api.get("/admin/config/signer-defaults"),
            ]);
            if (!signerDefaults) setSignerDefaults(defaultsResp.data);
            const clients = (wsResp.data?.clients || []).slice(0, 2);
            const today = new Date().toISOString().slice(0, 10);
            const next = [];
            clients.forEach((c, i) => {
                const first = (c.first_name || "").trim();
                const last = (c.last_name || "").trim();
                next.push({
                    signer_name: [first, last].filter(Boolean).join(" "),
                    signer_email: (c.email || "").trim(),
                    signer_role: i === 0 ? "Client" : "Co-Client",
                    signed_on: today,
                    signature_method: "zoho_sign",
                    notes: "",
                });
            });
            const d = defaultsResp.data || {};
            next.push({
                signer_name: d.name || "Phillip Toyin For Studio G4 Ltd",
                signer_email: d.email || "phillip@studio-g4.co.uk",
                signer_role: d.role || "Director",
                signed_on: today,
                signature_method: "zoho_sign",
                notes: "",
            });
            setSigners(next);
            toast.success(`Populated ${next.length} signer`
                + (next.length === 1 ? "" : "s") + " from project.");
        } catch (e) {
            const detail = e?.response?.data?.detail
                || e?.response?.data?.error || e.message;
            toast.error(`Could not populate from project: ${detail}`);
        } finally {
            setPopulating(false);
        }
    };

    const fileErrors = pdfFiles.map(_validateFile);
    const overCap = pdfFiles.length > MAX_FILES_SOFT_CAP;
    const hasAnyFileError = fileErrors.some(Boolean) || overCap;

    const submit = async () => {
        setErr("");
        if (pdfFiles.length === 0) {
            setErr("Please choose at least one PDF file.");
            return;
        }
        if (hasAnyFileError) {
            setErr("Fix the file errors highlighted below.");
            return;
        }
        if (!methodSummary.trim()) {
            setErr("Method summary is required.");
            return;
        }
        for (let i = 0; i < signers.length; i++) {
            const s = signers[i];
            if (!s.signer_name.trim()) { setErr(`Signer ${i + 1}: name is required.`); return; }
            if (!s.signer_role.trim()) { setErr(`Signer ${i + 1}: role is required.`); return; }
            if (!s.signed_on) { setErr(`Signer ${i + 1}: signed-on date is required.`); return; }
        }
        setBusy(true);
        try {
            const sharedEnvRef = externalRef.trim();
            const payload = signers.map(s => {
                const row = {
                    signer_name: s.signer_name.trim(),
                    signer_role: s.signer_role.trim(),
                    signed_on: s.signed_on,
                    signature_method: s.signature_method,
                };
                if (s.signer_email.trim()) row.signer_email = s.signer_email.trim();
                if (sharedEnvRef) row.external_envelope_ref = sharedEnvRef;
                if (s.notes.trim()) row.notes = s.notes.trim();
                return row;
            });
            const fd = new FormData();
            pdfFiles.forEach(f => fd.append("pdfs", f));
            fd.append("signers", JSON.stringify(payload));
            fd.append("method_summary", methodSummary.trim());
            if (sharedEnvRef) fd.append("external_ref", sharedEnvRef);
            fd.append("op_id", opId);
            const r = await axios.post(endpointUrl, fd, { withCredentials: true });
            if (onSuccessData) onSuccessData(r.data);
            onClose();
        } catch (e) {
            const d = e?.response?.data?.detail || e?.response?.data;
            setErr((d && (d.reason || d.message || d.error)) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-gray-900/60 backdrop-blur-sm"
             data-testid={`${testIdPrefix}-modal`}>
            <div className="w-full max-w-3xl bg-white rounded-xl shadow-2xl flex flex-col max-h-full overflow-hidden">

                {/* ======== HEADER ============================================= */}
                <div className="flex items-start justify-between p-6 border-b border-gray-200 bg-white z-10 shrink-0">
                    <div className="pr-6">
                        <h2 className="text-xl font-semibold text-gray-900">
                            {submitLabel}
                        </h2>
                        <p className="mt-1.5 text-sm text-gray-500 leading-relaxed">
                            {helpText}
                        </p>
                    </div>
                    <button type="button" onClick={onClose}
                            aria-label="Close modal"
                            data-testid={`${testIdPrefix}-close`}
                            className="text-gray-400 bg-transparent hover:bg-gray-100 hover:text-gray-900 rounded-lg p-1.5 ml-auto inline-flex items-center transition-colors shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* ======== BODY (scroll) ======================================= */}
                <div className="flex-1 overflow-y-auto bg-gray-50 p-6 space-y-8">

                    {/* ---- 1. PDFs ------------------------------------------- */}
                    <section>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                            PDFs <span className="text-red-500">*</span>
                        </label>
                        <div onDrop={onDrop} onDragOver={onDragOver}
                              onDragLeave={onDragLeave}
                              onClick={() => !busy && fileInputRef.current?.click()}
                              data-testid={`${testIdPrefix}-dropzone`}
                              className={"border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transition-all group "
                                  + (busy ? "opacity-60 cursor-not-allowed " : "cursor-pointer ")
                                  + (isDragOver
                                      ? "border-[#A8543F] bg-[#A8543F]/5 "
                                      : "border-gray-300 bg-white hover:bg-gray-50 hover:border-[#A8543F]/50 ")}>
                            <div className={"p-3 rounded-full mb-3 transition-colors "
                                + (isDragOver
                                    ? "bg-[#A8543F]/10"
                                    : "bg-gray-100 group-hover:bg-[#A8543F]/10")}>
                                <Upload className={"w-6 h-6 transition-colors "
                                    + (isDragOver
                                        ? "text-[#A8543F]"
                                        : "text-gray-500 group-hover:text-[#A8543F]")} />
                            </div>
                            <p className="text-sm text-gray-700 text-center mb-1">
                                <span className="font-semibold text-[#A8543F]">Click to browse</span> or drag and drop files here
                            </p>
                            <p className="text-xs text-gray-500 text-center">
                                25 MiB max per file; up to {MAX_FILES_SOFT_CAP} files per bundle.
                            </p>
                            <input ref={fileInputRef} type="file" accept="application/pdf" multiple
                                    data-testid={`${testIdPrefix}-pdf`}
                                    onChange={e => addFiles(e.target.files)}
                                    className="hidden" />
                        </div>

                        {pdfFiles.length > 0 && (
                            <div className="mt-3 flex flex-col gap-1.5"
                                 data-testid={`${testIdPrefix}-file-list`}>
                                {pdfFiles.map((f, i) => {
                                    const fe = fileErrors[i];
                                    return (
                                        <div key={`${f.name}-${f.size}-${f.lastModified}-${i}`}
                                             className={"flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm bg-white border "
                                                 + (fe ? "border-red-300 bg-red-50"
                                                       : "border-gray-200")}
                                             data-testid={`${testIdPrefix}-file-row-${i}`}>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-mono text-xs text-gray-700">
                                                    {i + 1}. {f.name}
                                                </div>
                                                <div className="text-[11px] text-gray-500 mt-0.5">
                                                    {_fmtSize(f.size)}
                                                    {fe && (
                                                        <span className="ml-2 text-red-700"
                                                              data-testid={`${testIdPrefix}-file-error-${i}`}>
                                                            {fe}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                                                    disabled={busy}
                                                    data-testid={`${testIdPrefix}-file-remove-${i}`}
                                                    className="shrink-0 text-gray-400 hover:text-red-600 p-1 transition-colors"
                                                    aria-label={`Remove ${f.name}`}>
                                                <X size={14} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {overCap && (
                            <div className="mt-2 text-xs text-red-700"
                                 data-testid={`${testIdPrefix}-overcap`}>
                                Too many files — remove some; soft cap {MAX_FILES_SOFT_CAP}.
                            </div>
                        )}
                    </section>

                    {/* ---- 2. Signers --------------------------------------- */}
                    <section>
                        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                            <label className="block text-sm font-semibold text-gray-900">
                                Signers <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center gap-2">
                                {projectId && (
                                    <button type="button"
                                            onClick={populateFromProject}
                                            disabled={busy || populating}
                                            data-testid={`${testIdPrefix}-populate-from-project`}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#A8543F] focus:ring-offset-1 focus:ring-offset-gray-50 transition-colors disabled:opacity-60">
                                        {populating ? "Loading…" : "Populate from Project"}
                                    </button>
                                )}
                                <button type="button" onClick={addSigner}
                                        disabled={busy}
                                        data-testid={`${testIdPrefix}-add-signer`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#A8543F] bg-[#A8543F]/10 rounded-md hover:bg-[#A8543F]/20 focus:outline-none focus:ring-2 focus:ring-[#A8543F] focus:ring-offset-1 focus:ring-offset-gray-50 transition-colors disabled:opacity-60">
                                    <Plus size={14} />
                                    Add signer
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col gap-4">
                            {signers.map((s, i) => (
                                <div key={i}
                                     data-testid={`${testIdPrefix}-signer-${i}`}
                                     className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-shadow hover:shadow-md">
                                    <div className="flex items-center justify-between px-5 py-3 bg-gray-50/80 border-b border-gray-100">
                                        <span className="text-xs font-bold text-gray-500 tracking-widest uppercase">
                                            Signer {i + 1}
                                        </span>
                                        {signers.length > 1 && (
                                            <button type="button" onClick={() => removeSigner(i)}
                                                    data-testid={`${testIdPrefix}-remove-signer-${i}`}
                                                    title="Remove signer"
                                                    aria-label={`Remove signer ${i + 1}`}
                                                    className="text-gray-400 hover:text-red-600 transition-colors p-1">
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                                        <div>
                                            <label className={LABEL_CLS}>Name <span className="text-red-500">*</span></label>
                                            <input type="text" value={s.signer_name}
                                                   onChange={e => updateSigner(i, { signer_name: e.target.value })}
                                                   placeholder="e.g. Jane Doe"
                                                   data-testid={`${testIdPrefix}-signer-${i}-name`}
                                                   className={INPUT_CLS} />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLS}>Email</label>
                                            <input type="email" value={s.signer_email}
                                                   onChange={e => updateSigner(i, { signer_email: e.target.value })}
                                                   placeholder="jane@example.com"
                                                   data-testid={`${testIdPrefix}-signer-${i}-email`}
                                                   className={INPUT_CLS} />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLS}>Role <span className="text-red-500">*</span></label>
                                            <input type="text" value={s.signer_role}
                                                   onChange={e => updateSigner(i, { signer_role: e.target.value })}
                                                   placeholder="e.g. Client, Co-Client, Director"
                                                   data-testid={`${testIdPrefix}-signer-${i}-role`}
                                                   className={INPUT_CLS} />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLS}>Signed on <span className="text-red-500">*</span></label>
                                            <input type="date" value={s.signed_on}
                                                   onChange={e => updateSigner(i, { signed_on: e.target.value })}
                                                   data-testid={`${testIdPrefix}-signer-${i}-signed-on`}
                                                   className={INPUT_CLS} />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className={LABEL_CLS}>Method <span className="text-red-500">*</span></label>
                                            <select value={s.signature_method}
                                                    onChange={e => updateSigner(i, { signature_method: e.target.value })}
                                                    data-testid={`${testIdPrefix}-signer-${i}-method`}
                                                    className={`${INPUT_CLS} bg-white`}>
                                                {Object.entries(EXT_METHOD_LABELS).map(([v, lbl]) => (
                                                    <option key={v} value={v}>{lbl}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className={LABEL_CLS}>Notes</label>
                                            <textarea rows={2} value={s.notes}
                                                      onChange={e => updateSigner(i, { notes: e.target.value })}
                                                      placeholder="Add any relevant context about this signature…"
                                                      data-testid={`${testIdPrefix}-signer-${i}-notes`}
                                                      className={`${INPUT_CLS} resize-y`} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ---- 3. Summary (shared envelope ref lives here) ------ */}
                    <section className="bg-gray-200/40 p-5 rounded-xl border border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Summary</h3>
                        <div className="space-y-4">
                            <div>
                                <label className={LABEL_CLS}>
                                    Method summary <span className="text-red-500">*</span>
                                </label>
                                <textarea rows={2} value={methodSummary}
                                          onChange={e => setMethodSummary(e.target.value)}
                                          data-testid={`${testIdPrefix}-method-summary`}
                                          placeholder="e.g. Signed via Zoho Sign envelope ZS-123 on 2026-01-15 by all parties"
                                          className={`${INPUT_CLS} bg-white resize-y`} />
                            </div>
                            <div>
                                <label className={LABEL_CLS}>
                                    External envelope ref
                                    <span className="ml-2 font-normal text-[11px] text-gray-500">
                                        Applied to every signer on this record.
                                    </span>
                                </label>
                                <input type="text" value={externalRef}
                                       onChange={e => setExternalRef(e.target.value)}
                                       placeholder="e.g. ZS-12345 / DocuSign envelope ID"
                                       data-testid={`${testIdPrefix}-external-ref`}
                                       className={`${INPUT_CLS} bg-white`} />
                            </div>
                        </div>
                    </section>

                    {err && (
                        <div data-testid={`${testIdPrefix}-error`}
                             className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                            {err}
                        </div>
                    )}
                </div>

                {/* ======== FOOTER ============================================ */}
                <div className="p-4 sm:p-5 border-t border-gray-200 bg-white flex flex-col-reverse sm:flex-row items-center justify-between gap-4 shrink-0">
                    <span className="text-[11px] text-gray-400 font-mono tracking-tight self-start sm:self-center">
                        op_id: {opId.slice(0, 18)}…
                    </span>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <button type="button" onClick={onClose} disabled={busy}
                                data-testid={`${testIdPrefix}-cancel`}
                                className="w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-all disabled:opacity-60">
                            Cancel
                        </button>
                        <button type="button" onClick={submit}
                                disabled={busy || pdfFiles.length === 0 || hasAnyFileError}
                                data-testid={`${testIdPrefix}-submit`}
                                style={{ backgroundColor: ACCENT }}
                                onMouseEnter={e => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = ACCENT_DARK)}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = ACCENT)}
                                className="w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-white border border-transparent rounded-lg shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A8543F] transition-all disabled:opacity-60">
                            {busy ? "Uploading…" : submitLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>);
}
